// frontend/src/App.tsx

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import Navbar from './components/Navbar';
import AuthModal from './components/AuthModal';
import HomePage from './pages/HomePage';
import MoviesPage from './pages/MoviesPage';
import MovieDetailPage from './pages/MovieDetailPage';
import SeasonPage from './pages/SeasonPage';
import SeasonsPage from './pages/SeasonsPage';
import QuizPage from './pages/PresciptionPage';
import ProfilePage from './pages/ProfilePage';
import AboutPage from './pages/AboutPage';
import SettingsPage from './pages/SettingsPage';
import UserPage from './pages/UserPage';
import NotificationsPage  from './pages/NotificationsPage';
import NotFoundPage from './pages/NotFoundPage';
import { Heart } from 'lucide-react';
import ScrollToTop from './components/ScrollToTop';
import LoginPage from './pages/LoginPage';
import { useLocation } from 'react-router-dom';
import ForgotPasswordPage  from './pages/ForgotPasswordPage';
import ResetPasswordPage  from './pages/ResetPasswordPage';
import VerifyEmailPage  from './pages/VerifyEmailPage';
import AdminPage from './pages/AdminPage';


import './index.css';


const AppContent: React.FC = () => {
  const { state } = useAppContext();
  const location = useLocation();

  if (state.isLoading && !state.currentUser) {
    return (
<div className="min-h-screen flex items-center justify-center bg-[#faf8f6]">
        <div className="text-center">
          <Heart className="w-16 h-16 text-romcom-red fill-current animate-pulse mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f6]">

      <ScrollToTop />
      {location.pathname !== '/login' && <Navbar />}

      <AuthModal />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/movies" element={<MoviesPage />} />
        <Route path="/movies/:id" element={<MovieDetailPage />} />
        <Route path="/seasons/:season" element={<SeasonPage />} />
        <Route path="/seasons" element={<SeasonsPage />} />
        <Route path="/prescriptor" element={<QuizPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/users/:username" element={<UserPage />} />
        <Route path="/users/:username" element={<ProfilePage />} />
        <Route path="*" element={<NotFoundPage />} />
        <Route path="/login" element={<LoginPage />} />
        
        <Route path="/profile/:username" element={<ProfilePage />} />
        <Route path="/profile" element={<ProfilePage />} /> 
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
       <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/admin" element={<AdminPage />} />
       
      
      </Routes>

      <footer className="bg-[#faf8f6] border-t border-romcom-pink/20 mt-20 py-12">

        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="mb-6 md:mb-0">
              <div className="flex items-center space-x-3">
                <img src="/logo2.jpeg" alt="RomComThis" className="w-16 h-16 object-contain" />
                <div>
                  <h2 className="text-lg font-playfair font-bold text-romcom-red -mt-1">ROMCOMTHIS</h2>
                  <p className="text-2xl font-playfair font-bold text-gray-800">For the Love of RomComs</p>
                </div>
              </div>
              <p className="mt-4 text-gray-600 max-w-md">
                A cozy corner for romantic comedy enthusiasts.
                Discover, rate, and share your favorite feel-good movies.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-12">
              <div>
                <h3 className="font-bold text-gray-800 mb-4">Explore</h3>
                <ul className="space-y-2">
                  <li><Link to="/movies" className="text-gray-600 hover:text-romcom-red transition-colors">Movies</Link></li>
                  <li><Link to="/seasons" className="text-gray-600 hover:text-romcom-red transition-colors">Seasons</Link></li>
                  <li><Link to="/prescriptor" className="text-gray-600 hover:text-romcom-red transition-colors">Prescriptor</Link></li>
                  <li><Link to="/profile" className="text-gray-600 hover:text-romcom-red transition-colors">Profile</Link></li>
                  <li><Link to="/about" className="text-gray-600 hover:text-romcom-red transition-colors">About</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 mb-4">Discover</h3>
                <ul className="space-y-2">
                  <li><Link to="/seasons/winter" className="text-gray-600 hover:text-romcom-red transition-colors">❄️ Winter</Link></li>
                  <li><Link to="/seasons/spring" className="text-gray-600 hover:text-romcom-red transition-colors">🌸 Spring</Link></li>
                  <li><Link to="/seasons/summer" className="text-gray-600 hover:text-romcom-red transition-colors">☀️ Summer</Link></li>
                  <li><Link to="/seasons/autumn" className="text-gray-600 hover:text-romcom-red transition-colors">🍂 Autumn</Link></li>
                  <li><Link to="/movies?holiday=christmas" className="text-gray-600 hover:text-romcom-red transition-colors">🎄 Christmas</Link></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-200 text-center text-gray-600">
            <p className="mb-2">Made with 💖 for romcom lovers everywhere</p>
            <p className="text-sm">© {new Date().getFullYear()} For the Love of Romcoms. All movie data is for educational purposes.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <AppProvider>
      <Router>
        <AppContent />
      </Router>
    </AppProvider>
  );
}

export default App;